/**
 * MoreFromMyInvestments — the attorney-approved "More from My Investments"
 * affiliate section on the landing page (hotfix-1-161).
 *
 * Three sibling-product cards (Finance Dashboard, Music, Marketing). Card COPY is
 * fixed and attorney-signed-off (legal/affiliate-advertising-copy.md §1); PRICES
 * are fetched LIVE from the backend (`GET /api/v1/affiliate/products`, which proxies
 * the shared payments service) so admin price changes reflect immediately.
 *
 * Each card shows a "from" price line — "From R{amount}/month" formatted from
 * from_price_cents + currency — or "Coming soon" when `available: false` (no real
 * price set yet, or the price lookup failed). The whole section is decorative and
 * pre-auth; it degrades gracefully and never blocks the landing page render.
 *
 * Brand: light/vibrant white bg, aurora gradient accents (violet → pink → orange),
 * NO dark navy, NO emoji icons.
 */

import { useEffect, useState } from 'react';
import { getAffiliateProducts, type AffiliateProduct } from '../services/api';

/** Attorney-approved sub-heading (legal/affiliate-advertising-copy.md §1). */
const SECTION_SUB =
  'Real tools for your money, your music and your marketing.';

/**
 * Attorney-approved promotional perk line (legal/affiliate-advertising-copy.md §1).
 * Frames the subscriber free entry as a COMPLIMENTARY promotional thank-you — never
 * "buy a chance to win" (Lotteries Act). Paid skill attempts remain a separate route,
 * so we deliberately do NOT claim entries are never purchased.
 */
const SECTION_PERK =
  'Active subscribers of any My Investments service may receive a complimentary competition entry as a promotional thank-you.';

const AURORA = 'linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f97316 100%)';

/**
 * Format a monthly "from" price from cents + ISO-4217 currency.
 * ZAR → "From R99/month", USD → "From $99/month", others → "$" fallback.
 * Mirrors utils/format.formatEntryFee's symbol convention.
 */
export function formatFromPrice(cents: number, currency: string): string {
  const symbol = currency === 'ZAR' ? 'R' : '$';
  const amount = (cents / 100).toLocaleString('en-ZA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `From ${symbol}${amount}/month`;
}

function ProductCard({ product }: { product: AffiliateProduct }) {
  const priced =
    product.available && product.from_price_cents != null && product.currency != null;

  return (
    <li className="group h-full">
      <div className="flex h-full flex-col rounded-2xl border border-violet-100 bg-white p-6 shadow-sm transition hover:border-violet-200 hover:shadow-md">
        {/* Gradient accent bar — brand flourish, decorative */}
        <span
          className="mb-5 block h-1.5 w-12 rounded-full"
          style={{ background: AURORA }}
          aria-hidden="true"
        />
        <h3 className="font-heading text-lg font-bold text-violet-900">
          {product.name}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-600">
          {product.blurb}
        </p>
        <p className="mt-4 text-sm font-bold text-violet-700">
          {priced ? (
            formatFromPrice(product.from_price_cents!, product.currency!)
          ) : (
            <span className="text-gray-400">Coming soon</span>
          )}
        </p>
        <a
          href={product.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Learn more about ${product.name} (opens in a new tab)`}
          className="mt-4 inline-flex w-fit cursor-pointer items-center gap-1 rounded-button text-sm font-semibold text-violet-600 transition hover:text-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-300"
        >
          Learn more
          <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
            →
          </span>
        </a>
      </div>
    </li>
  );
}

export function MoreFromMyInvestments() {
  const [products, setProducts] = useState<AffiliateProduct[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getAffiliateProducts()
      .then((r) => {
        // Defensive: a malformed/non-array response must never crash the landing page.
        setProducts(Array.isArray(r.data) ? r.data : []);
        setLoaded(true);
      })
      .catch(() => {
        // Decorative section — never break the landing page. Hide on failure.
        setProducts([]);
        setLoaded(true);
      });
  }, []);

  // Nothing to show (load failed or empty) → render nothing rather than an empty band.
  if (loaded && products.length === 0) {
    return null;
  }

  return (
    <section
      id="more-from-my-investments"
      className="bg-surface-2 py-16 scroll-mt-20"
      data-testid="more-from-my-investments"
      aria-labelledby="more-from-heading"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h2
            id="more-from-heading"
            className="font-heading text-2xl font-bold text-violet-900 sm:text-3xl"
          >
            More from My Investments
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-gray-600">{SECTION_SUB}</p>
          <p
            className="mx-auto mt-4 max-w-2xl rounded-2xl bg-violet-50 px-5 py-3 text-sm font-semibold text-violet-800"
            data-testid="subscriber-perk-line"
          >
            {SECTION_PERK}
          </p>
        </div>

        {!loaded ? (
          // Lightweight skeletons while prices load — aria-hidden so SR users skip them.
          <ul
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
            role="list"
            aria-hidden="true"
          >
            {[0, 1, 2].map((i) => (
              <li key={i} className="h-full">
                <div className="flex h-full flex-col gap-3 rounded-2xl border border-violet-100 bg-white p-6 shadow-sm">
                  <div className="h-1.5 w-12 animate-pulse rounded-full bg-gray-200" />
                  <div className="h-5 w-3/4 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-full animate-pulse rounded bg-gray-100" />
                  <div className="h-4 w-1/3 animate-pulse rounded bg-gray-200" />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <ul
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
            role="list"
          >
            {products.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </ul>
        )}

        <p className="mx-auto mt-8 max-w-2xl text-center text-xs italic text-gray-500">
          Each service stands on its own and is charged at its ordinary advertised price.
        </p>
      </div>
    </section>
  );
}

export default MoreFromMyInvestments;
